/**
 * Serwis do obsługi kaucji
 * Zarządzanie kaucjami i ich przypisaniami do produktów
 */

import api from './api';

const depositService = {
  /**
   * Pobiera listę wszystkich kaucji
   * @param {boolean} includeInactive - czy dołączyć nieaktywne kaucje
   */
  async getDeposits(includeInactive = false) {
    try {
      const response = await api.get('/deposits', {
        params: { include_inactive: includeInactive }
      });
      const data = response.data;

      if (data.success) {
        return { success: true, data: data.data.deposits };
      }
      return { success: false, message: data.message };
    } catch (error) {
      console.error('Błąd pobierania kaucji:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Pobiera szczegóły kaucji wraz z przypisanymi produktami
   * @param {number} depositId - ID kaucji
   */
  async getDeposit(depositId) {
    try {
      const response = await api.get(`/deposits/${depositId}`);
      const data = response.data;

      if (data.success) {
        return { success: true, data: data.data.deposit };
      }
      return { success: false, message: data.message };
    } catch (error) {
      console.error('Błąd pobierania kaucji:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Tworzy nową kaucję
   * @param {Object} depositData - dane kaucji (nazwa, kwota, opis, create_product)
   */
  async createDeposit(depositData) {
    try {
      const response = await api.post('/deposits', depositData);
      const data = response.data;

      if (data.success) {
        return { success: true, data: data.data.deposit };
      }
      return { success: false, message: data.message };
    } catch (error) {
      console.error('Błąd tworzenia kaucji:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Aktualizuje kaucję
   * @param {number} depositId - ID kaucji
   * @param {Object} depositData - dane do aktualizacji
   */
  async updateDeposit(depositId, depositData) {
    try {
      const response = await api.put(`/deposits/${depositId}`, depositData);
      const data = response.data;

      if (data.success) {
        return { success: true };
      }
      return { success: false, message: data.message };
    } catch (error) {
      console.error('Błąd aktualizacji kaucji:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Usuwa (dezaktywuje) kaucję
   * @param {number} depositId - ID kaucji
   * @param {boolean} force - czy usunąć trwale (true) czy tylko dezaktywować (false)
   */
  async deleteDeposit(depositId, force = false) {
    try {
      const params = force ? { force: true } : {};

      const response = await api.delete(`/deposits/${depositId}`, { params });
      const data = response.data;

      if (data.success) {
        return { success: true, message: data.message };
      }
      return { success: false, message: data.message || 'Błąd API' };
    } catch (error) {
      console.error('Błąd usuwania kaucji:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Pobiera produkty przypisane do kaucji
   * @param {number} depositId - ID kaucji
   */
  async getDepositProducts(depositId) {
    try {
      const response = await api.get(`/deposits/${depositId}/products`);
      const data = response.data;

      if (data.success) {
        return { success: true, data: data.data.products };
      }
      return { success: false, message: data.message };
    } catch (error) {
      console.error('Błąd pobierania produktów kaucji:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Przypisuje produkty do kaucji
   * @param {number} depositId - ID kaucji
   * @param {number[]} productIds - tablica ID produktów
   */
  async assignProducts(depositId, productIds) {
    try {
      const response = await api.post(`/deposits/${depositId}/products`, { product_ids: productIds });
      const data = response.data;

      if (data.success) {
        return { success: true, data: data.data };
      }
      return { success: false, message: data.message };
    } catch (error) {
      console.error('Błąd przypisywania produktów:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Usuwa przypisanie produktu z kaucji
   * @param {number} depositId - ID kaucji
   * @param {number} productId - ID produktu
   */
  async removeProduct(depositId, productId) {
    try {
      const response = await api.delete(`/deposits/${depositId}/products/${productId}`);
      const data = response.data;

      if (data.success) {
        return { success: true };
      }
      return { success: false, message: data.message };
    } catch (error) {
      console.error('Błąd usuwania produktu z kaucji:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * Sprawdza czy produkt ma przypisaną kaucję (dla POS)
   * @param {number} productId - ID produktu
   */
  async checkProductDeposit(productId) {
    try {
      const response = await api.get(`/deposits/check-product/${productId}`);
      const data = response.data;

      if (data.success) {
        return {
          success: true,
          hasDeposit: data.data.has_deposit,
          deposit: data.data.deposit
        };
      }
      return { success: false, hasDeposit: false, deposit: null, failed: true };
    } catch (error) {
      // failed:true odroznia "sprawdzenie sie NIE UDALO" od "produkt nie ma kaucji".
      // Bez tego blad (np. 403) byl polykany po cichu i kaucja nie trafiala na
      // paragon, a kasjer nie mial o tym pojecia.
      console.error('Błąd sprawdzania kaucji produktu:', error);
      return { success: false, hasDeposit: false, deposit: null, failed: true };
    }
  },

  /**
   * Sprawdza kaucje dla wielu produktów na raz (dla POS)
   * @param {number[]} productIds - tablica ID produktów
   */
  async checkProductsDeposits(productIds) {
    try {
      const response = await api.post('/deposits/check-products', { product_ids: productIds });
      const data = response.data;

      if (data.success) {
        return { success: true, deposits: data.data.deposits };
      }
      return { success: false, deposits: {} };
    } catch (error) {
      console.error('Błąd sprawdzania kaucji produktów:', error);
      return { success: false, deposits: {} };
    }
  }
};

export default depositService;
